import { apiClient } from './apiClient';

/**
 * Authorization API Service
 * Handles all API calls related to RBAC (Roles, Policies, Users)
 */
export const authorizationApi = {
  // ==================== POLICIES ====================
  
  /**
   * Get all policies (read-only from policies.json)
   */
  getPolicies: async () => {
    return apiClient.request('/auth/policies', {
      method: 'GET'
    });
  },

  /**
   * Get a specific policy by ID
   */
  getPolicy: async (policyId) => {
    return apiClient.request(`/auth/policies/${policyId}`, {
      method: 'GET'
    });
  },

  // ==================== ROLES ====================
  
  /**
   * Get all roles
   */
  getRoles: async () => {
    return apiClient.request('/auth/roles', {
      method: 'GET'
    });
  },

  /**
   * Get a specific role by ID
   */
  getRole: async (roleId) => {
    return apiClient.request(`/auth/roles/${roleId}`, {
      method: 'GET'
    });
  },

  /**
   * Create a new role
   * @param {Object} data - Role data (role_name, description)
   */
  createRole: async (data) => {
    return apiClient.request('/auth/roles', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  /**
   * Update an existing role
   * @param {string} roleId - Role ID
   * @param {Object} data - Updated role data
   */
  updateRole: async (roleId, data) => {
    return apiClient.request(`/auth/roles/${roleId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  /**
   * Delete a role
   * @param {string} roleId - Role ID to delete
   */
  deleteRole: async (roleId) => {
    return apiClient.request(`/auth/roles/${roleId}`, {
      method: 'DELETE'
    });
  },

  /**
   * Attach a policy to a role
   * @param {string} roleId - Role ID
   * @param {string} policyId - Policy ID to attach
   */
  attachPolicy: async (roleId, policyId) => {
    return apiClient.request(`/auth/roles/${roleId}/policies/${policyId}`, {
      method: 'POST'
    });
  },

  /**
   * Detach a policy from a role
   * @param {string} roleId - Role ID
   * @param {string} policyId - Policy ID to detach
   */
  detachPolicy: async (roleId, policyId) => {
    return apiClient.request(`/auth/roles/${roleId}/policies/${policyId}`, {
      method: 'DELETE'
    });
  },

  // ==================== USERS ====================
  
  /**
   * Get all users from database
   */
  getUsers: async () => {
    return apiClient.request('/auth/users', {
      method: 'GET'
    });
  },

  /**
   * Get a specific user by sub
   * @param {string} userSub - User sub (from Cognito JWT)
   */
  getUser: async (userSub) => {
    return apiClient.request(`/auth/users/${userSub}`, {
      method: 'GET'
    });
  },

  /**
   * Create a new user in database (sync from Cognito)
   * @param {Object} data - User data (user_sub, user_email)
   */
  createUser: async (data) => {
    return apiClient.request('/auth/users', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  /**
   * Assign a role to a user
   * @param {string} userSub - User sub
   * @param {string} roleId - Role ID to assign
   */
  assignRole: async (userSub, roleId) => {
    return apiClient.request(`/auth/users/${userSub}/roles/${roleId}`, {
      method: 'POST'
    });
  },

  /**
   * Remove a role from a user
   * @param {string} userSub - User sub
   * @param {string} roleId - Role ID to remove
   */
  removeRole: async (userSub, roleId) => {
    return apiClient.request(`/auth/users/${userSub}/roles/${roleId}`, {
      method: 'DELETE'
    });
  },

  // ==================== PERMISSIONS ====================
  
  /**
   * Get current user's permissions
   */
  getMyPermissions: async () => {
    return apiClient.request('/auth/my-permissions', {
      method: 'GET'
    });
  },

  /**
   * Get current user's roles
   */
  getMyRoles: async () => {
    return apiClient.request('/auth/my-roles', {
      method: 'GET'
    });
  },

  /**
   * Check if current user has a specific permission
   * @param {string} resource - Resource name
   * @param {string} action - Action name
   */
  checkPermission: async (resource, action) => {
    return apiClient.request('/auth/check-permission', {
      method: 'POST',
      body: JSON.stringify({ resource, action })
    });
  },

  // ==================== PASSWORD POLICY ====================

  /**
   * Get the Cognito password policy for the user pool
   */
  getPasswordPolicy: async () => {
    return apiClient.request('/auth/password-policy', {
      method: 'GET'
    });
  },

  // ==================== COGNITO INTEGRATION FOR AUTHORIZATION ====================

  /**
   * List all Cognito users for authorization management
   * Returns users with sub, email, username, and status
   */
  getCognitoUsers: async () => {
    return apiClient.request('/auth/cognito/users', {
      method: 'GET'
    });
  },

  /**
   * Sync all Cognito users to the auth_users collection
   * Creates user records for users that don't exist
   */
  syncCognitoUsers: async () => {
    return apiClient.request('/auth/cognito/sync', {
      method: 'POST'
    });
  },

  /**
   * Sync a single Cognito user to the auth_users collection
   * @param {string} userSub - User's Cognito sub
   */
  syncSingleCognitoUser: async (userSub) => {
    return apiClient.request(`/auth/cognito/sync/${userSub}`, {
      method: 'POST'
    });
  }
};

